import React, { useMemo } from "react"
import { TextStyle, StyleProp } from "~/ui/primitives"
import { Text } from "~components/El"

/** 匹配 ... + 十六进制哈希，例如 ...4dbcb5355f */
const HASH_PATTERN = /\.\.\.[0-9a-fA-F]+/g

type Segment = {
  text: string
  highlight?: boolean
}

/** 从哈希体中取末尾两位数字（从右往左扫描，中间可夹杂字母） */
function splitHashLastTwoDigits(hashBody: string): {
  prefix: string
  digits: string
  suffix: string
} {
  const digitIndices: number[] = []
  for (let i = hashBody.length - 1; i >= 0 && digitIndices.length < 2; i--) {
    if (/\d/.test(hashBody[i])) {
      digitIndices.unshift(i)
    }
  }

  if (digitIndices.length === 0) {
    return { prefix: hashBody, digits: "", suffix: "" }
  }

  const first = digitIndices[0]
  const last = digitIndices[digitIndices.length - 1]
  return {
    prefix: hashBody.slice(0, first),
    digits: digitIndices.map((i) => hashBody[i]).join(""),
    suffix: hashBody.slice(last + 1),
  }
}

export function parseHashHighlightText(text: string): Segment[] {
  if (!text) return []

  const segments: Segment[] = []
  let lastIndex = 0
  const re = new RegExp(HASH_PATTERN.source, "g")
  let match: RegExpExecArray | null

  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ text: text.slice(lastIndex, match.index) })
    }

    const full = match[0]
    const ellipsis = "..."
    const hashBody = full.slice(ellipsis.length)
    const { prefix, digits, suffix } = splitHashLastTwoDigits(hashBody)

    segments.push({ text: ellipsis + prefix })
    if (digits) {
      segments.push({ text: digits, highlight: true })
    }
    if (suffix) {
      segments.push({ text: suffix })
    }

    lastIndex = match.index + full.length
  }

  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex) })
  }

  return segments
}

export interface HashHighlightTextProps {
  text: string
  color?: string
  fontSize?: number | string
  highlightColor?: string
  fontWeight?: TextStyle["fontWeight"]
  highlightFontWeight?: TextStyle["fontWeight"]
  /** 字符间距，默认 2 */
  letterSpacing?: number
  style?: StyleProp<TextStyle>
  highlightStyle?: StyleProp<TextStyle>
  numberOfLines?: number
}

/**
 * 解析文本中的区块哈希（...xxxxxxxx），高亮哈希末尾两位数字。
 * 例：...4dbcb5355f → ...4dbcb53 + 55(高亮) + f
 */
const HashHighlightText: React.FC<HashHighlightTextProps> = ({
  text,
  color = "#fff",
  fontSize = 14,
  highlightColor = "#ffff00",
  fontWeight,
  highlightFontWeight = "700",
  letterSpacing = 1.3,
  style,
  highlightStyle,
  numberOfLines,
}) => {
  const segments = useMemo(() => parseHashHighlightText(text), [text])
  const baseTextStyle = useMemo(
    () => ({ letterSpacing, ...(style as TextStyle) }),
    [letterSpacing, style],
  )
  const highlightBadgeStyle = useMemo<TextStyle>(
    () => ({
      backgroundColor: highlightColor,
      borderRadius: 3,
      paddingLeft: 2,
      paddingRight: 2,
      ...(highlightStyle as TextStyle),
    }),
    [highlightColor, highlightStyle],
  )

  if (!segments.length) {
    return null
  }

  return (
    <Text
      color={color}
      fontSize={fontSize}
      fontWeight={fontWeight}
      style={baseTextStyle as never}
      numberOfLines={numberOfLines}
    >
      {segments.map((segment, index) =>
        segment.highlight ? (
          <Text
            key={index}
            color="#000"
            fontSize={fontSize}
            fontWeight={highlightFontWeight}
            style={highlightBadgeStyle as never}
          >
            {segment.text}
          </Text>
        ) : (
          <Text key={index}>{segment.text}</Text>
        ),
      )}
    </Text>
  )
}

export default HashHighlightText
